'use client';

import Link from 'next/link';
import Image from 'next/image';
import { memo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { CATEGORY_SLUG_TO_NAME } from '@/lib/category-slugs';
// import { useTranslation } from 'react-i18next';

// Kategoriler
const categories = [
  {
    id: 'berber-koltuklari',
    name: 'Berber Koltukları',
    seoAnchor: 'erkek kuaför dekorasyon için berber koltukları',
    image: '/images/barber-chair.webp',
    description: 'Modern ve şık berber koltukları',
    count: 12,
    color: 'bg-red-50',
    textColor: 'text-red-800',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    id: 'berber-tezgahlari',
    name: 'Berber Tezgahları',
    seoAnchor: 'berber tezgahı ve berber tezgahları modelleri',
    image: '/images/salon-station.webp',
    description: 'Fonksiyonel ve estetik tezgahlar',
    count: 8,
    color: 'bg-blue-50',
    textColor: 'text-blue-800',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    id: 'yikama-setleri',
    name: 'Yıkama Setleri',
    seoAnchor: 'berber yıkama seti çözümleri',
    image: '/images/washing-unit.webp',
    description: 'Konforlu saç yıkama üniteleri',
    count: 6,
    color: 'bg-purple-50',
    textColor: 'text-purple-800',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    id: 'bekleme-koltuklari',
    name: 'Bekleme Koltukları',
    seoAnchor: 'salon bekleme koltuğu modelleri',
    image: '/images/waiting-seats.webp',
    description: 'Ergonomik bekleme koltukları',
    count: 4,
    color: 'bg-green-50',
    textColor: 'text-green-800',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
  },
];

interface FeaturedCategoriesProps {
  className?: string;
}

const FeaturedCategories = ({ className }: FeaturedCategoriesProps) => {
  return (
    <section className={cn("relative bg-gradient-to-b from-stone-100/30 to-stone-50/20 overflow-hidden", className)}>
      {/* Ambient Red Spotlight Glows */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/5 to-transparent rounded-full opacity-30 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 relative z-10 py-12 md:py-16">
        <div className="text-center mb-8 max-w-3xl mx-auto">
          <div
            className="inline-flex items-center bg-red-100 text-red-950 text-sm font-medium px-4 py-1.5 rounded-full mb-4"
          >
            <span className="flex h-2 w-2 rounded-full bg-red-800 mr-2 shrink-0" aria-hidden />
            Salon Mobilya Çözümleri
          </div>

          <h2
            className="categories-title text-3xl md:text-5xl font-bold mb-6"
          >
            <span className="text-black">Lider Berber Koltukları ve </span>
            <span className="text-red-800">Kuaför Tezgahları Üreticisi</span>
          </h2>

          <p
            className="categories-desc text-center text-gray-700 mx-auto"
            style={{ fontSize: "1.1rem", maxWidth: "600px", lineHeight: "1.6" }}
          >
            Filiz Kozmetik olarak 20 yılı aşkın tecrübemizle berber tezgahları, berber koltukları, kuaför tezgahları ve
            kuaför koltukları başta olmak üzere premium salon ekipmanları üretiyoruz. Tüm ürünlerimiz uzun ömürlü kullanım için tasarlanmaktadır.
          </p>
          <p className="mt-4 text-sm text-gray-700">
            Populer aramalar:
            {' '}
            <Link href="/urunler/kategori/berber-tezgahlari" className="text-red-800 underline underline-offset-2">
              berber tezgahi
            </Link>
            {' '}-
            {' '}
            <Link href="/urunler/kategori/kuafor-tezgahlari" className="text-red-800 underline underline-offset-2">
              kuafor tezgahi
            </Link>
            {' '}-
            {' '}
            <Link href="/urunler/kategori/berber-koltuklari" className="text-red-800 underline underline-offset-2">
              erkek kuafor dekorasyon
            </Link>
            {' '}-
            {' '}
            <Link href="/urunler/kategori/makyaj-koltuklari" className="text-red-800 underline underline-offset-2">
              guzellik salonu dekorasyon
            </Link>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {categories.map((category, index) => (
            <FeaturedCategoryCard
              key={category.id}
              category={category}
            />
          ))}
        </div>

        {/* Tüm Kategoriler - SEO Link Yapısı */}
        <div className="mt-16 pt-10 border-t border-gray-200/60 text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-6">A'dan Z'ye Tüm Kategorilerimiz</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(CATEGORY_SLUG_TO_NAME).map(([slug, name]) => (
              <Link 
                key={slug} 
                href={`/urunler/kategori/${slug}`}
                className="bg-white/80 hover:bg-white text-gray-700 hover:text-red-800 font-medium px-4 py-2 rounded-full border border-gray-200/80 shadow-sm hover:shadow hover:border-red-200 transition-all text-sm min-h-[44px] flex items-center"
              >
                {name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Modern ve göz alıcı kategori kartı
type FeaturedCategory = {
  id: string;
  name: string;
  seoAnchor: string;
  image: string;
};

const FeaturedCategoryCard = memo(function FeaturedCategoryCard({
  category,
}: {
  category: FeaturedCategory;
}) {
  return (
    <Link href={`/urunler/kategori/${category.id}`} aria-label={category.seoAnchor} className="block h-full">
      <div
        className="group relative rounded-2xl overflow-hidden shadow-md cursor-pointer transform-gpu h-full"
      >
        {/* Görsel */}
        <div className="relative w-full aspect-[4/5] h-64 sm:h-72 md:h-80 lg:h-96 bg-gray-100">
          <Image
            src={category.image}
            alt={category.name?.trim() || 'Berber ve kuaför mobilya kategorisi'}
            fill
            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-[1.03] will-change-transform"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
            quality={75}
            priority={true}
          />
          {/* Base Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
          {/* Hover Darken Overlay - Animating opacity is much cheaper than animating gradient */}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          
          {/* İçerik */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 px-4 pointer-events-none">
            <h3 className="text-white text-xl sm:text-2xl font-bold mb-4 drop-shadow-md text-center">{category.name}</h3>
            <span
              className="inline-block px-5 py-2 rounded-full bg-white/95 text-primary font-semibold text-sm shadow-sm opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
            >
              Hepsini Gör
            </span>
            <span className="sr-only">{category.seoAnchor}</span>
          </div>
        </div>
      </div>
    </Link>
  );
});

export default FeaturedCategories;